import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { FaBell, FaChartLine, FaEye, FaCloudDownloadAlt } from "react-icons/fa";
import OddsDetailsDialog from "./oddsDetailsDialog";
import OddMovementDialog from "./OddMovementDialog";
import moment from "moment-timezone";

const ACTION_ITEMS = [
  { id: "details", label: "View details", icon: FaEye },
  { id: "movement", label: "Track movement", icon: FaChartLine },
  { id: "alerts", label: "Set alert", icon: FaBell },
  { id: "check", label: "Check odds", icon: FaCloudDownloadAlt },
];

const getPrimaryBookmaker = (match) =>
  match?.base?.[0] ?? match?.reference?.[0] ?? null;
const getPrimaryReferenceBookmaker = (match) => match?.reference?.[0] ?? null;

const splitTeams = (matchup = "") => {
  if (!matchup) {
    return ["Unknown Home", "Unknown Away"];
  }

  const separators = [" vs ", " VS ", " v ", " - "];
  const separator = separators.find((item) => matchup.includes(item));

  if (!separator) {
    return [matchup, ""];
  }

  const [home, away] = matchup.split(separator);
  return [home?.trim() ?? "Unknown Home", away?.trim() ?? "Unknown Away"];
};

const getAsianValue = (rows = [], side) => {
  const targetHeader = side === "home" ? "1" : "2";
  const item = rows.find((row) => row?.header === targetHeader) ?? null;

  if (!item) {
    return { prefix: side === "home" ? "H" : "A", value: "N/A", odds: "" };
  }

  return {
    prefix: side === "home" ? "H" : "A",
    value: item.handicap ?? "N/A",
    odds: item.odds ?? "",
  };
};

const getGoalValue = (rows = [], side) => {
  const targetHeader = side === "over" ? "Over" : "Under";
  const item = rows.find((row) => row?.header === targetHeader) ?? null;

  if (!item) {
    return { prefix: side === "over" ? "O" : "U", value: "N/A", odds: "" };
  }

  return {
    prefix: side === "over" ? "O" : "U",
    value: item.name ?? "N/A",
    odds: item.odds ?? "",
  };
};

const TooltipButton = ({ icon: Icon, label, onClick }) => (
  <div className="group relative">
    <button
      type="button"
      onClick={onClick}
      className="flex h-7 w-7 items-center justify-center rounded-md border border-white/20 bg-white/90 text-customAccentBlue shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:bg-customSky hover:text-customAccentBlue focus:outline-none focus:ring-2 focus:ring-customAccent/30 dark:border-custom_dark_secondary dark:bg-custom_dark_primary dark:text-customAccent dark:hover:bg-custom_dark_background"
      aria-label={label}
      title={label}
    >
      <Icon className="text-[11px]" />
    </button>
    <span className="pointer-events-none absolute left-1/2 top-full z-20 mt-2 -translate-x-1/2 whitespace-nowrap rounded-md bg-customPredawn px-2 py-1 text-[10px] font-semibold text-white opacity-0 shadow-md transition-opacity duration-200 group-hover:opacity-100 group-focus-within:opacity-100 dark:bg-custom_dark_primary">
      {label}
    </span>
  </div>
);

const MarketCell = ({ data, tone = "blue" }) => {
  const toneClass =
    tone === "red"
      ? "text-customFontRed"
      : tone === "green"
        ? "text-customDarkGreen"
        : "text-customAccentBlue";

  return (
    <div className="text-center text-xs font-bold text-customInk dark:text-customFontMilkWhite">
      <div className="flex flex-col items-center justify-center">
        <div className="flex gap-1">
          <span className={`${toneClass}`}>{data.prefix}</span>{" "}
          <span>{data.value}</span>
        </div>
        {data.odds ? (
          <span className="text-customSecondaryText/90 dark:text-customMuted">
            {" "}
            ({data.odds})
          </span>
        ) : null}
      </div>
    </div>
  );
};

const OddsTableView = ({
  matches = [],
  selectedDate,
  highlightedEvents = {},
  highlightAnimationName,
}) => {
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [movementMatch, setMovementMatch] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof document === "undefined") {
      return false;
    }

    return (
      document.documentElement.classList.contains("dark") ||
      sessionStorage.getItem("smartReportTheme") === "dark"
    );
  });
  const rowHighlightAnimationName = `${highlightAnimationName}Row`;

  useEffect(() => {
    if (typeof document === "undefined") {
      return undefined;
    }

    const syncDarkMode = () => {
      setIsDarkMode(
        document.documentElement.classList.contains("dark") ||
          sessionStorage.getItem("smartReportTheme") === "dark"
      );
    };

    syncDarkMode();

    const observer = new MutationObserver(syncDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);

  // const groupedMatches = useMemo(() => {
  //   const groups = new Map();

  //   matches.forEach((match) => {
  //     const league = match?.league ?? 'Unknown League';

  //     if (!groups.has(league)) {
  //       groups.set(league, []);
  //     }

  //     groups.get(league).push(match);
  //   });

  //   return Array.from(groups.entries()).map(([league, leagueMatches]) => ({
  //     league,
  //     matches: leagueMatches,
  //   }));
  // }, [matches]);

  // Show all matches in a single group.
  // Keep the existing table rendering unchanged and simply skip LeagueRow.
  const groupedMatches = useMemo(() => {
    return [
      {
        league: "all",
        matches: Array.isArray(matches) ? matches : [],
      },
    ];
  }, [matches]);

  const handlePlaceholderAction = () => {
    alert("coming soon!");
  };

  return (
    <div className="overflow-hidden rounded-2xl border border-customBorder/20 bg-white shadow-sorting dark:border-custom_dark_secondary dark:bg-custom_dark_primary2">
      <style>
        {`
          @keyframes ${rowHighlightAnimationName} {
            0%, 100% { background-color: rgba(253, 186, 116, 0.28); }
            50% { background-color: #ffde219c; }
          }
        `}
      </style>
      <div className="overflow-x-auto overflow-y-hidden">
        <table className="min-w-[1240px] w-full border-collapse">
          <thead className="sticky top-0 z-10">
            <tr className="bg-gradient-to-r from-background via-background/90 to-background/80 text-customFontMilkWhite dark:from-custom_dark_background dark:via-custom_dark_primary dark:to-custom_dark_background">
              <th className="w-[84px] px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.18em]">
                Time
              </th>
              <th className="w-[200px] px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.18em]">
                Event
              </th>
              <th className="w-[10px] px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.18em]">
                
              </th>
              <th className="w-[130px] px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.18em]">
                League
              </th>
              <th className="w-[120px] px-3 py-2 text-center text-[11px] font-semibold uppercase tracking-[0.18em]">
                Rapid
              </th>
              <th className="w-[120px] px-3 py-2 text-center text-[11px] font-semibold uppercase tracking-[0.18em]">
                555Mix
              </th>
              <th className="w-[80px] px-3 py-2 text-center text-[11px] font-semibold uppercase tracking-[0.18em]">
                Home
              </th>
              <th className="w-[80px] px-3 py-2 text-center text-[11px] font-semibold uppercase tracking-[0.18em]">
                Away
              </th>
              <th className="w-[80px] px-3 py-2 text-center text-[11px] font-semibold uppercase tracking-[0.18em]">
                Over
              </th>
              <th className="w-[80px] px-3 py-2 text-center text-[11px] font-semibold uppercase tracking-[0.18em]">
                Under
              </th>
              <th className="w-[145px] px-3 py-2 text-center text-[11px] font-semibold uppercase tracking-[0.18em]">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {groupedMatches.map((group) => (
              <React.Fragment key={group.league}>
                {/* <LeagueRow league={group.league} /> */}
                {group.matches.map((match, index) => {
                  const bookmaker = getPrimaryBookmaker(match);
                  const referenceBookmaker =
                    getPrimaryReferenceBookmaker(match);
                  const eventId = String(match?.eventId ?? match?.id ?? "");
                  const isHighlighted = Boolean(highlightedEvents[eventId]);
                  const [homeTeam, awayTeam] = splitTeams(bookmaker?.matchup);
                  const homeOdds = getAsianValue(
                    bookmaker?.asian_handicap,
                    "home",
                  );
                  const awayOdds = getAsianValue(
                    bookmaker?.asian_handicap,
                    "away",
                  );
                  const overOdds = getGoalValue(bookmaker?.goal_line, "over");
                  const underOdds = getGoalValue(bookmaker?.goal_line, "under");
                  const rowBackgroundClass = isHighlighted
                    ? "bg-orange-100 dark:bg-amber-400/10"
                    : "odd:bg-[#e7eefc] even:bg-[#d7e2f7] dark:odd:bg-custom_dark_primary2 dark:even:bg-custom_dark_background";
                  const idleBackgroundColor = isHighlighted
                    ? isDarkMode
                      ? "rgba(251, 191, 36, 0.10)"
                      : "rgba(255, 237, 213, 1)"
                    : index % 2 === 0
                      ? isDarkMode
                        ? "#1B263B"
                        : "#e7eefc"
                      : isDarkMode
                        ? "#0D1B2A"
                        : "#d7e2f7";
                  const activeHighlightColor = isDarkMode
                    ? "rgba(250, 204, 21, 0.22)"
                    : "#ffde219c";

                  return (
                    <motion.tr
                      layout
                      key={`${group.league}-${eventId || match.id || index}`}
                      className={`border-b border-customLightMilkWhite transition-colors hover:bg-customSky/80 dark:border-custom_dark_secondary/80 dark:hover:bg-custom_dark_primary ${rowBackgroundClass}`}
                      initial={false}
                      animate={
                        isHighlighted
                          ? {
                              backgroundColor: [
                                idleBackgroundColor,
                                activeHighlightColor,
                                idleBackgroundColor,
                              ],
                              scale: [1, 1.01, 1],
                            }
                          : {
                              backgroundColor: idleBackgroundColor,
                              scale: 1,
                            }
                      }
                      transition={{
                        layout: {
                          duration: 0.35,
                          ease: "easeInOut",
                        },
                        backgroundColor: {
                          duration: 1.1,
                          ease: "easeInOut",
                          repeat: isHighlighted ? 4 : 0,
                        },
                        scale: {
                          duration: 0.45,
                          ease: "easeOut",
                        },
                      }}
                      style={
                        isHighlighted
                          ? {
                              animation: `${rowHighlightAnimationName} 1s ease-in-out 15`,
                            }
                          : undefined
                      }
                    >
                      <td className="px-3 py-1 align-center">
                        <div className="flex gap-3 items-center">
                          {match?.isSetAlarm && (
                            <FaBell className="text-lg text-customFontGold" />
                          )}
                          <div className="flex flex-col text-[11px] font-semibold leading-4">
                            <span className="uppercase tracking-[0.12em] text-customFontRed">
                              {match.status}
                            </span>
                            <span className="text-customPrimaryText dark:text-customFontMilkWhite">
                              {match.kickoff
                                ? moment(match.kickoff).format("hh:mm A")
                                : "TBD"}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-1 align-center">
                        <div className="flex flex-col gap-1">
                          <div className="flex flex-col text-paragraph font-semibold leading-5 ">
                            <span
                              className={`${match.homeUpper ? "text-customFontRed" : "text-customInk dark:text-customFontMilkWhite"}`}
                            >
                              {homeTeam}
                            </span>
                            <span
                              className={`${!match.homeUpper ? "text-customFontRed" : "text-customInk dark:text-customFontMilkWhite"}`}
                            >
                              {awayTeam || "TBD"}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-1 align-center">
                          {match?.oddFlag === "different" && (
                            <span className="text-customFontRed font-bold">
                              *
                            </span>
                          )}
                      </td>
                      <td className="px-3 py-1 align-center">
                        <div className="flex flex-col text-xs italic font-semibold leading-5 ">
                          <span className="text-customInk dark:text-customFontMilkWhite">
                            {match.league}
                          </span>
                        </div>
                      </td>
                      <td className="px-2 py-1 align-center relative">
                        {/* <div className="flex flex-col items-center justify-center gap-1 text-xs font-semibold text-customPrimaryText dark:text-customFontMilkWhite">
                          <span className="w-full rounded bg-white/80 px-2 py-1 text-center shadow-sm dark:bg-custom_dark_primary/80">
                            Body {bookmaker?.burmese_handicap?.body ?? 'N/A'}
                          </span>
                          <span className="w-full rounded bg-white/80 px-2 py-1 text-center shadow-sm dark:bg-custom_dark_primary/80">
                            Goal {bookmaker?.burmese_handicap?.goal ?? 'N/A'}
                          </span>
                        </div> */}
                          <div
                            className="bg-white/80 px-2 py-1 text-center text-xs font-bold text-customInk shadow-sm dark:border dark:border-custom_dark_secondary/70 dark:bg-custom_dark_primary dark:text-customFontMilkWhite"
                          >
                            <span>
                              {bookmaker?.burmese_handicap?.body ?? "N/A"} /{" "}
                              {bookmaker?.burmese_handicap?.goal ?? "N/A"}
                            </span>
                          </div>
                      </td>
                      <td className="px-2 py-1 align-center">
                        {/* <div className="flex flex-col items-center justify-center gap-1 text-xs font-semibold text-customPrimaryText dark:text-customFontMilkWhite">
                          <span className="w-full rounded bg-white/80 px-2 py-1 text-center shadow-sm dark:bg-custom_dark_primary/80">
                            Body {referenceBookmaker?.burmese_handicap?.body ?? 'N/A'}
                          </span>
                          <span className="w-full rounded bg-white/80 px-2 py-1 text-center shadow-sm dark:bg-custom_dark_primary/80">
                            Goal {referenceBookmaker?.burmese_handicap?.goal ?? 'N/A'}
                          </span>
                        </div> */}
                        <div className="bg-white/80 px-2 py-1 text-center text-xs font-bold shadow-sm dark:border dark:border-custom_dark_secondary/70 dark:bg-custom_dark_primary dark:text-customFontMilkWhite">
                          {referenceBookmaker ? (
                            <span>
                              {referenceBookmaker?.burmese_handicap?.body ??
                                "N/A"}{" "}
                              /{" "}
                              {referenceBookmaker?.burmese_handicap?.goal ??
                                "N/A"}
                            </span>
                          ) : (
                            <span className="text-customInk/40 dark:text-customMuted">N/A</span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-1 align-center">
                        <MarketCell data={homeOdds} tone="blue" />
                      </td>
                      <td className="px-3 py-1 align-center">
                        <MarketCell data={awayOdds} tone="red" />
                      </td>
                      <td className="px-3 py-1 align-center">
                        <MarketCell data={overOdds} tone="green" />
                      </td>
                      <td className="px-3 py-1 align-center">
                        <MarketCell data={underOdds} tone="blue" />
                      </td>
                      <td className="px-3 py-1 align-center">
                        <div className="flex flex-wrap justify-center gap-1 pt-1">
                          {ACTION_ITEMS.map(({ id, label, icon }) => (
                            <TooltipButton
                              key={`${match.id ?? index}-${id}`}
                              icon={icon}
                              label={label}
                              onClick={() => {
                                if (id === "details") {
                                  setSelectedMatch(match);
                                  return;
                                }
                                if (id === "movement") {
                                  setMovementMatch(match);
                                  return;
                                }

                                handlePlaceholderAction(label);
                              }}
                            />
                          ))}
                        </div>
                      </td>
                    </motion.tr>
                  );
                })}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {selectedMatch && (
        <OddsDetailsDialog
          open={Boolean(selectedMatch)}
          onClose={() => setSelectedMatch(null)}
          league={selectedMatch.league}
          kickoff={selectedMatch.kickoff}
          base={selectedMatch.base}
          reference={selectedMatch.reference}
        />
      )}

      {movementMatch && (
        <OddMovementDialog
          open={Boolean(movementMatch)}
          onClose={() => setMovementMatch(null)}
          match={movementMatch}
          selectedDate={selectedDate}
        />
      )}
    </div>
  );
};

export default OddsTableView;
